import { Button, Card, Checkbox, Dropdown, Input, RadioSet, Text } from '@ui/themed';
import { t } from 'i18next';
import { memo, useMemo, useState } from 'react';
import { SparkJsUpgradeAlert } from '../../../components/sparkjs/sparkjs-upgrade-alert';
import type { CardBadge } from '../../../components/ui/card/types';
import type { DropdownOption } from '../../../components/ui/dropdown/types';
import {
  type ActivePlanKey,
  type FeatureFlag,
  featureRestricted,
  features,
  getActiveBillingPlanKey,
} from '../../../constants/features';
import { isSparkJsFeatureUnlocked } from '../../../constants/sparkjs-features';
import { useEnv } from '../../../context/env-context';
import type { I18nPrefix } from '../../../i18n/prefix';
import { useStorefrontOptionsControls } from '../context/storefront-options-context';
import type {
  CheckoutElement,
  CheckoutElementTranslations,
  CheckoutStringAttribute,
  StandardCheckoutElement,
} from '../types/spark-options';
import {
  getCustomCheckoutElementErrors,
  getFileExtensionErrors,
} from '../utils/checkout-element-validation';
import { entryValue } from '../utils/locale-rows';
import { isElementHiddenByVersionGate } from '../utils/version-gated-elements';
import { CheckoutDropdownOptionsEditor } from './checkout-dropdown-options-editor';
import { ListEditor } from './list-editor';
import { PerLocaleFieldsEditor } from './per-locale-fields-editor';
import { SortableList } from './sortable-list';
import { withTooltip } from './with-tooltip';

type StandardElement = Extract<CheckoutElement, { kind: 'standard' }>;
type CustomElement = Extract<CheckoutElement, { kind: 'custom' }>;

const FIELD_STATUSES = ['hidden', 'optional', 'required', 'disabled'] as const;
type FieldStatus = (typeof FIELD_STATUSES)[number];

const EDITOR =
  'features:storefront_options.components.checkout_elements_editor' satisfies I18nPrefix;

const CHECKOUT_TYPES = [
  'text',
  'text-area',
  'number',
  'date',
  'email',
  'tel',
  'checkbox',
  'select',
  'week',
  'url',
  'time',
  'month',
  'color',
  // Plan-gated types go last, so a disabled option never leaves a gap mid-list.
  'file',
] as const;

type CheckoutType = (typeof CHECKOUT_TYPES)[number];

const CHECKOUT_TYPE_FEATURES: Partial<Record<CheckoutType, FeatureFlag>> = {
  file: features.FILE_UPLOAD,
};

function buildTypeOptions(plan?: ActivePlanKey): DropdownOption[] {
  return CHECKOUT_TYPES.map((type) => {
    const feature = CHECKOUT_TYPE_FEATURES[type];
    return {
      value: type,
      label: t(`${EDITOR}.type_options.${type}`),
      disabled: feature !== undefined && featureRestricted(feature, plan),
    };
  });
}

function typeCategory(type: string | undefined): 'checkbox' | 'file' | 'select' | 'text' {
  if (type === 'checkbox' || type === 'file' || type === 'select') return type;
  return 'text';
}

function isNumericType(type: string | undefined): boolean {
  return type === 'number' || type === 'date';
}

function firstTitle(translations: CheckoutElementTranslations): string {
  for (const { entries } of translations) {
    const title = entryValue(entries, 'title');
    if (title.trim()) return title;
  }
  return '';
}

const STANDARD_TITLE_KEYS = {
  'po-number': 'po_number',
  'shipping-requested-date': 'shipping_requested_date',
  'customer-reference': 'customer_reference',
} as const satisfies Record<StandardCheckoutElement, string>;

function standardLabel(id: StandardCheckoutElement): string {
  return t(`${EDITOR}.standard.${STANDARD_TITLE_KEYS[id]}`);
}

function deriveFieldStatus(flags: {
  hidden?: boolean;
  disabled?: boolean;
  required?: boolean;
}): FieldStatus {
  if (flags.hidden) return 'hidden';
  if (flags.disabled) return 'disabled';
  return flags.required ? 'required' : 'optional';
}

function fieldStatusBadgeVariant(status: FieldStatus): 'default' | 'green' | 'orange' {
  switch (status) {
    case 'hidden':
    case 'disabled':
      return 'default';
    case 'optional':
      return 'green';
    case 'required':
      return 'orange';
  }
}

function fieldStatusBadge(status: FieldStatus): CardBadge {
  return { text: t(`${EDITOR}.${status}_badge`), variant: fieldStatusBadgeVariant(status) };
}

function statusFlags(status: FieldStatus): {
  hidden: boolean;
  required: boolean;
  disabled: boolean;
} {
  return {
    hidden: status === 'hidden',
    required: status === 'required',
    disabled: status === 'disabled',
  };
}

function fieldStatusOption<T extends FieldStatus>(value: T) {
  return {
    value,
    label: t(`${EDITOR}.statuses.${value}.label`),
    description: t(`${EDITOR}.statuses.${value}.description`),
  };
}

type RowProps<T> = {
  element: T;
  onChange: (next: T) => void;
  defaultExpanded?: boolean;
};

type RemovableRowProps<T> = RowProps<T> & {
  onRemove: () => void;
};

type CustomRowProps = RemovableRowProps<CustomElement> & {
  baseTypeOptions: DropdownOption[];
};

function StandardRow({ element, onChange, defaultExpanded }: RowProps<StandardElement>) {
  const status = deriveFieldStatus(element);
  const statusOptions = useMemo(() => FIELD_STATUSES.map(fieldStatusOption), []);

  function updateStatus(nextStatus: FieldStatus) {
    onChange({ ...element, ...statusFlags(nextStatus) });
  }

  return (
    <Card size="sm" hideBackground hidePadding initialCollapsed={!defaultExpanded}>
      <Card.Header
        headingLevel="h3"
        collapsible
        badges={[
          { text: t(`${EDITOR}.standard_badge`), variant: 'blue' },
          fieldStatusBadge(status),
        ]}
      >
        {standardLabel(element.id)}
      </Card.Header>

      <Card.Body>
        <RadioSet<FieldStatus>
          label={t(`${EDITOR}.field_status`)}
          value={status}
          onChange={updateStatus}
          options={statusOptions}
        />
      </Card.Body>
    </Card>
  );
}

function CustomRow({
  element,
  onChange,
  onRemove,
  defaultExpanded,
  baseTypeOptions,
}: CustomRowProps) {
  const { showValidationErrors: showErrors } = useStorefrontOptionsControls();
  const { attributes } = element;
  const category = typeCategory(attributes.type);
  const status = deriveFieldStatus(element.attributes);
  const title =
    firstTitle(element.translations) || element.name.trim() || t(`${EDITOR}.custom_field_fallback`);
  const errors = useMemo(
    () => (showErrors ? getCustomCheckoutElementErrors(element) : {}),
    [showErrors, element],
  );
  const minMaxErrorKey = attributes.type === 'date' ? 'invalid_date' : 'invalid_number';
  const fileExtensionErrors = useMemo(
    () =>
      showErrors
        ? getFileExtensionErrors(attributes.fileextensions).map((invalid) =>
            invalid ? t(`${EDITOR}.invalid_file_extension`) : undefined,
          )
        : undefined,
    [showErrors, attributes.fileextensions],
  );

  const typeOptions = useMemo(() => {
    // Custom types can't be added, but a field already saved with a legacy one keeps it selectable.
    const currentType = attributes.type;
    if (currentType && !(CHECKOUT_TYPES as readonly string[]).includes(currentType)) {
      return [...baseTypeOptions, { value: currentType, label: currentType }];
    }

    return baseTypeOptions;
  }, [attributes.type, baseTypeOptions]);
  const statusOptions = useMemo(() => FIELD_STATUSES.map(fieldStatusOption), []);

  function updateStringAttr(key: CheckoutStringAttribute, value: string) {
    const nextAttributes = { ...attributes };
    if (value.trim() === '') delete nextAttributes[key];
    else nextAttributes[key] = value;
    onChange({ ...element, attributes: nextAttributes });
  }

  function updateStatus(nextStatus: FieldStatus) {
    onChange({ ...element, attributes: { ...attributes, ...statusFlags(nextStatus) } });
  }

  return (
    <Card size="sm" hideBackground hidePadding initialCollapsed={!defaultExpanded}>
      <Card.Header
        headingLevel="h3"
        collapsible
        badges={[
          { text: t(`${EDITOR}.custom_badge`), variant: 'purple' },
          fieldStatusBadge(status),
        ]}
        primaryAction={{
          variant: 'dangerBare',
          icon: 'delete',
          ariaLabel: t(`${EDITOR}.remove`),
          onClick: onRemove,
        }}
      >
        {title}
      </Card.Header>

      <Card.Body>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <Input
            label={t(`${EDITOR}.fields.name`)}
            description={t(`${EDITOR}.fields.name_hint`)}
            placeholder={t(`${EDITOR}.placeholders.name`)}
            value={element.name}
            error={errors.name ? t(`${EDITOR}.name_required`) : undefined}
            onChange={(value) => onChange({ ...element, name: value })}
          />
          <Dropdown
            label={t(`${EDITOR}.fields.type`)}
            options={typeOptions}
            value={attributes.type ?? ''}
            onChange={(next) => updateStringAttr('type', next)}
          />
        </div>

        <PerLocaleFieldsEditor
          label={t(`${EDITOR}.translations`)}
          description={t(`${EDITOR}.translations_hint`)}
          value={element.translations}
          onChange={(next) => onChange({ ...element, translations: next })}
          namedFields={[
            {
              key: 'title',
              label: t(`${EDITOR}.fields.title`),
              placeholder: t(`${EDITOR}.placeholders.title`),
              required: true,
            },
            {
              key: 'detail',
              label: t(`${EDITOR}.fields.detail`),
              placeholder: t(`${EDITOR}.placeholders.detail`),
            },
          ]}
          allowFreeform
          requireLocale
        />

        {category === 'checkbox' && (
          <Checkbox
            label={t(`${EDITOR}.fields.value`)}
            checked={attributes.value === 'true'}
            onChange={() => updateStringAttr('value', attributes.value === 'true' ? '' : 'true')}
          />
        )}

        {category === 'text' && (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Input
              label={t(`${EDITOR}.fields.placeholder`)}
              placeholder={t(`${EDITOR}.placeholders.placeholder`)}
              value={attributes.placeholder ?? ''}
              onChange={(value) => updateStringAttr('placeholder', value)}
            />
            <Input
              label={t(`${EDITOR}.fields.value`)}
              value={attributes.value ?? ''}
              onChange={(value) => updateStringAttr('value', value)}
            />
            <Input
              label={t(`${EDITOR}.fields.minlength`)}
              placeholder={t(`${EDITOR}.placeholders.minlength`)}
              value={attributes.minlength ?? ''}
              error={errors.minlength ? t(`${EDITOR}.invalid_number`) : undefined}
              onChange={(value) => updateStringAttr('minlength', value)}
            />
            <Input
              label={t(`${EDITOR}.fields.maxlength`)}
              placeholder={t(`${EDITOR}.placeholders.maxlength`)}
              value={attributes.maxlength ?? ''}
              error={errors.maxlength ? t(`${EDITOR}.invalid_number`) : undefined}
              onChange={(value) => updateStringAttr('maxlength', value)}
            />
            <Input
              label={t(`${EDITOR}.fields.pattern`)}
              placeholder={t(`${EDITOR}.placeholders.pattern`)}
              value={attributes.pattern ?? ''}
              error={errors.pattern ? t(`${EDITOR}.invalid_pattern`) : undefined}
              onChange={(value) => updateStringAttr('pattern', value)}
            />
            {isNumericType(attributes.type) && (
              <>
                <Input
                  label={t(`${EDITOR}.fields.min`)}
                  placeholder={t(`${EDITOR}.placeholders.min`)}
                  value={attributes.min ?? ''}
                  error={errors.min ? t(`${EDITOR}.${minMaxErrorKey}`) : undefined}
                  onChange={(value) => updateStringAttr('min', value)}
                />
                <Input
                  label={t(`${EDITOR}.fields.max`)}
                  placeholder={t(`${EDITOR}.placeholders.max`)}
                  value={attributes.max ?? ''}
                  error={errors.max ? t(`${EDITOR}.${minMaxErrorKey}`) : undefined}
                  onChange={(value) => updateStringAttr('max', value)}
                />
                <Input
                  label={t(`${EDITOR}.fields.step`)}
                  placeholder={t(`${EDITOR}.placeholders.step`)}
                  value={attributes.step ?? ''}
                  error={errors.step ? t(`${EDITOR}.invalid_number`) : undefined}
                  onChange={(value) => updateStringAttr('step', value)}
                />
              </>
            )}
          </div>
        )}

        {category === 'file' && (
          <>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Input
                label={t(`${EDITOR}.fields.min_files`)}
                placeholder={t(`${EDITOR}.placeholders.min_files`)}
                value={attributes.minlength ?? ''}
                error={errors.minlength ? t(`${EDITOR}.invalid_number`) : undefined}
                onChange={(value) => updateStringAttr('minlength', value)}
              />
              <Input
                label={t(`${EDITOR}.fields.max_files`)}
                placeholder={t(`${EDITOR}.placeholders.max_files`)}
                value={attributes.maxlength ?? ''}
                error={errors.maxlength ? t(`${EDITOR}.invalid_number`) : undefined}
                onChange={(value) => updateStringAttr('maxlength', value)}
              />
              <Input
                label={t(`${EDITOR}.fields.maxsize`)}
                placeholder={t(`${EDITOR}.placeholders.maxsize`)}
                value={attributes.maxsize ?? ''}
                error={errors.maxsize ? t(`${EDITOR}.invalid_file_size`) : undefined}
                onChange={(value) => updateStringAttr('maxsize', value)}
              />
            </div>
            <ListEditor
              label={t(`${EDITOR}.fields.fileextensions`)}
              values={attributes.fileextensions ?? []}
              onChange={(values) =>
                onChange({
                  ...element,
                  attributes: {
                    ...attributes,
                    fileextensions: values.length > 0 ? values : undefined,
                  },
                })
              }
              placeholder={t(`${EDITOR}.placeholders.fileextension`)}
              errors={fileExtensionErrors}
            />
          </>
        )}

        {category === 'select' && (
          <CheckoutDropdownOptionsEditor
            value={element.options ?? []}
            onChange={(options) =>
              onChange({ ...element, options: options.length > 0 ? options : undefined })
            }
          />
        )}

        <RadioSet<FieldStatus>
          label={t(`${EDITOR}.field_status`)}
          value={status}
          onChange={updateStatus}
          options={statusOptions}
        />
      </Card.Body>
    </Card>
  );
}

export const CheckoutElementsEditor = memo(function CheckoutElementsEditor({
  values,
  onChange,
}: {
  values: CheckoutElement[];
  onChange: (values: CheckoutElement[]) => void;
}) {
  const [autoExpandKey, setAutoExpandKey] = useState<string | null>(null);
  const env = useEnv();

  const gatedElementsUnlocked = isSparkJsFeatureUnlocked(
    env.sparkjs_version,
    'CHECKOUT_STANDARD_ELEMENTS',
  );

  const planKey = env.billing.plan_key;
  const baseTypeOptions = useMemo(
    () => buildTypeOptions(getActiveBillingPlanKey(planKey)),
    [planKey],
  );

  function isHiddenByVersionGate(element: CheckoutElement): boolean {
    return isElementHiddenByVersionGate(element, env.sparkjs_version);
  }

  const visibleValues = values.filter((element) => !isHiddenByVersionGate(element));

  function getKey(element: CheckoutElement, index: number) {
    return element.kind === 'standard' ? `standard-${element.id}` : `custom-${index}`;
  }

  function updateAt(index: number, next: CheckoutElement) {
    onChange(visibleValues.map((element, i) => (i === index ? next : element)));
  }

  function removeAt(index: number) {
    onChange(visibleValues.filter((_, i) => i !== index));
  }

  function addElement(element: CustomElement) {
    setAutoExpandKey(getKey(element, visibleValues.length));
    onChange([...visibleValues, element]);
  }

  const hasUnnamedCustom = visibleValues.some(
    (element) => element.kind === 'custom' && element.name.trim() === '',
  );

  return (
    <div className="flex flex-col gap-3">
      {!gatedElementsUnlocked && (
        <SparkJsUpgradeAlert
          feature="CHECKOUT_STANDARD_ELEMENTS"
          featureLabel={t(`${EDITOR}.version_gate.feature_label`)}
        />
      )}

      {visibleValues.length === 0 && (
        <Text size="sm" variant="secondary">
          {t(`${EDITOR}.empty`)}
        </Text>
      )}

      <SortableList
        items={visibleValues}
        getKey={getKey}
        onReorder={onChange}
        renderItem={(element, index) =>
          element.kind === 'standard' ? (
            <StandardRow
              element={element}
              onChange={(next) => updateAt(index, next)}
              defaultExpanded={getKey(element, index) === autoExpandKey}
            />
          ) : (
            <CustomRow
              element={element}
              onChange={(next) => updateAt(index, next)}
              onRemove={() => removeAt(index)}
              defaultExpanded={getKey(element, index) === autoExpandKey}
              baseTypeOptions={baseTypeOptions}
            />
          )
        }
      />

      <div className="flex flex-wrap gap-2">
        {withTooltip(
          hasUnnamedCustom && t(`${EDITOR}.add_custom_disabled_hint`),
          <Button
            variant="secondary"
            icon="plusCircle"
            disabled={hasUnnamedCustom}
            onClick={() =>
              addElement({
                kind: 'custom',
                name: '',
                translations: [],
                attributes: { type: 'text' },
              })
            }
          >
            {t(`${EDITOR}.add_custom`)}
          </Button>,
        )}
      </div>
    </div>
  );
});
